<template>
    <div class="og">
        <section v-for="group in groups" :key="group.id" class="og__group">
            <header class="og__head">
                <div class="og__title">
                    <h5>{{ group.name }}</h5>
                    <span v-if="showRequired && group.is_required" class="og__tag">Required</span>
                    <span v-else-if="showRequired && group.selection_type === 'multiple'" class="og__tag og__tag--muted">Optional</span>
                </div>
                <div class="og__selected">
                    <template v-if="summary(group)">
                        {{ summary(group) }}
                        <span v-if="showPrices && adjustment(group)" class="og__delta">{{ signedPrice(adjustment(group)) }}</span>
                        <button v-if="clearable" type="button" class="og__clear" @click="$emit('clear', group)">Clear</button>
                    </template>
                    <span v-else-if="clearable" class="og__hint">{{ emptyHint }}</span>
                </div>
            </header>

            <p v-if="showDescriptions && group.description" class="og__desc">{{ group.description }}</p>

            <!-- Color swatches -->
            <div v-if="group.is_color" class="pm-swatches">
                <button
                    v-for="option in group.options"
                    :key="option.id"
                    type="button"
                    class="pm-swatch"
                    :class="{ 'is-selected': isSelected(group.id, option.id) }"
                    :aria-label="option.label"
                    :aria-pressed="isSelected(group.id, option.id)"
                    :title="option.label"
                    @click="$emit('toggle', group, option)"
                >
                    <span class="pm-swatch__color" :style="{ backgroundColor: option.value || '#D4D4D8' }" />
                    <span class="pm-swatch__label">{{ option.label }}</span>
                    <span v-if="showPrices && option.price_adjustment" class="pm-swatch__price">{{ signedPrice(option.price_adjustment) }}</span>
                </button>
            </div>

            <!-- Text / image cards -->
            <div v-else class="pm-cards">
                <button
                    v-for="option in group.options"
                    :key="option.id"
                    type="button"
                    class="pm-card"
                    :class="{ 'is-selected': isSelected(group.id, option.id), 'has-image': !!option.image_url }"
                    :aria-pressed="isSelected(group.id, option.id)"
                    :title="option.description || option.label"
                    @click="$emit('toggle', group, option)"
                >
                    <img v-if="option.image_url" :src="option.image_url" :alt="option.label" class="pm-card__thumb" />
                    <span class="pm-card__body">
                        <span class="pm-card__row">
                            <span class="pm-card__label">{{ option.label }}</span>
                            <span v-if="showPrices && option.price_adjustment" class="pm-card__price">{{ signedPrice(option.price_adjustment) }}</span>
                        </span>
                        <span v-if="option.level" class="pm-card__level">{{ option.level }}</span>
                    </span>
                    <span class="pm-card__check" aria-hidden="true">
                        <svg width="10" height="8" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.3819 0.742676L6.10461 11.8998L2.25731 8.06381L0.763672 9.55745L6.20645 15.0002L20 2.32686L18.3819 0.742676Z" fill="currentColor"/>
                        </svg>
                    </span>
                </button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { defineEmits, defineProps } from 'vue';

/**
 * Option groups (modifiers) as compact cards / swatches. Shared by the product page
 * (with prices) and the quotation request form (no prices, clearable, nothing preselected).
 */
defineProps({
    groups: { type: Array, default: () => [] },
    isSelected: { type: Function, required: true },   // (groupId, optionId) => boolean
    summary: { type: Function, required: true },      // (group) => 'Velvet, Oak'
    adjustment: { type: Function, default: () => 0 }, // (group) => number
    signedPrice: { type: Function, default: (v) => String(v) },
    showPrices: { type: Boolean, default: true },
    showRequired: { type: Boolean, default: true },
    showDescriptions: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    emptyHint: { type: String, default: 'Not sure? Leave it — we will advise.' },
});

defineEmits(['toggle', 'clear']);
</script>

<style scoped>
.og { display: grid; gap: 22px; }

.og__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.og__title { display: flex; align-items: baseline; gap: 10px; }
.og__title h5 { font-size: 15px; font-weight: 600; line-height: 1.2; margin: 0; }

.og__tag {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #bb976d;
}
.og__tag--muted { color: #8a9197; }

.og__selected {
    font-size: 13px;
    color: #3c474e;
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
}
.og__delta { font-weight: 600; color: #bb976d; }
.og__hint { color: #8a9197; font-size: 12.5px; }
.og__clear {
    font-size: 12px;
    color: #8a9197;
    text-decoration: underline;
    text-underline-offset: 2px;
}
.og__clear:hover { color: #bb976d; }
.og__desc { margin: -4px 0 10px; font-size: 13px; color: #8a9197; }

/* Cards */
.pm-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 8px;
}

.pm-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 54px;
    padding: 9px 12px;
    text-align: left;
    background: #fff;
    border: 1px solid #e8e9ea;
    border-radius: 10px;
    transition: border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
}
.pm-card:hover { border-color: #bb976d; }
.pm-card.is-selected {
    border-color: #bb976d;
    background: rgba(187, 151, 109, 0.08);
    box-shadow: inset 0 0 0 1px #bb976d;
}

.pm-card__thumb {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: 8px;
    object-fit: cover;
    border: 1px solid rgba(23, 36, 48, 0.08);
}
.pm-card__body { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 2px; }
.pm-card__row { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.pm-card__label { font-size: 14px; font-weight: 500; line-height: 1.25; color: #172430; }
.pm-card__price { flex: 0 0 auto; font-size: 13px; font-weight: 600; color: #bb976d; white-space: nowrap; }
.pm-card__level { font-size: 12px; line-height: 1.2; color: #8a9197; }

.pm-card__check {
    position: absolute;
    top: -7px;
    right: -7px;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #bb976d;
    color: #fff;
    box-shadow: 0 0 0 2px #fff;
    opacity: 0;
    transform: scale(0.6);
    transition: opacity 0.18s ease, transform 0.18s ease;
}
.pm-card.is-selected .pm-card__check { opacity: 1; transform: scale(1); }

/* Swatches */
.pm-swatches { display: flex; flex-wrap: wrap; gap: 6px 14px; }
.pm-swatch {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 64px;
    padding: 4px 0;
    text-align: center;
}
.pm-swatch__color {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 1px solid rgba(23, 36, 48, 0.12);
    box-shadow: 0 0 0 2px #fff;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
}
.pm-swatch:hover .pm-swatch__color { transform: scale(1.06); }
.pm-swatch.is-selected .pm-swatch__color { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #bb976d; }
.pm-swatch__label { font-size: 11.5px; line-height: 1.15; color: #3c474e; }
.pm-swatch.is-selected .pm-swatch__label { color: #172430; font-weight: 600; }
.pm-swatch__price { font-size: 11px; font-weight: 600; color: #bb976d; margin-top: -3px; }

@media (max-width: 520px) {
    .pm-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .pm-card__label { font-size: 13px; }
}
</style>

<style>
/* Dark mode (the .dark class lives on <html>, out of reach of the scoped block). */
.dark .og__selected, .dark .og__hint, .dark .pm-swatch__label { color: #cfd4d8; }
.dark .pm-card { background: #1e2a35; border-color: #3c474e; }
.dark .pm-card.is-selected { background: rgba(187, 151, 109, 0.16); }
.dark .pm-card__label, .dark .pm-swatch.is-selected .pm-swatch__label { color: #fff; }
.dark .pm-swatch__color, .dark .pm-card__check { box-shadow: 0 0 0 2px #1e2a35; }
.dark .pm-swatch.is-selected .pm-swatch__color { box-shadow: 0 0 0 2px #1e2a35, 0 0 0 4px #bb976d; }
</style>
